// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * "Det, der blokerer" as data — Gennemgang's left tile, its J/K order and
 * its tab badge: the types that keep the report a kladde, each with every
 * reason it blocks (prototype `blockReasons`) — an open sample, no approval
 * yet, no tons — rather than the server's one (`SurveyBlockingType.reason`,
 * sample > review > mass).
 *
 * Which types block is the server's call when the fractions are read
 * (`GET /survey/fractions` `blocking`, in type-id order); the reasons are the
 * client's read of the type joined with the server's (the server never lists
 * a rejected type). Before the fractions land the list is derived from the
 * types alone, rejected ones left out.
 *
 * Pure: no React, no DOM.
 */

import type { Sample, SurveyFractions, SurveyType } from '../api/types';
import type { Tone } from '../kortlaegning/vocab';
import { reportedTypes } from './checklist';
import { samplesOfType } from './parts';
import { REVIEW_LABEL } from './review';

export type BlockReason = 'sample' | 'review' | 'mass';

/** The order reasons are listed in (the server's precedence). */
const REASON_ORDER: readonly BlockReason[] = ['sample', 'review', 'mass'];

/** A reason's pill. Shorter than `BLOCKING_STATUS` (the fraction table's), since a row may carry three. */
export const BLOCK_REASON: Record<BlockReason, { tone: Tone; text: string }> = {
  sample: { tone: 'wait', text: 'Prøve' },
  review: { tone: 'warn', text: 'Ikke godkendt' },
  mass: { tone: 'warn', text: 'Mangler tons' },
};

/**
 * Why a type blocks, in `REASON_ORDER`; empty when it does not. A sample
 * blocks while the type waits for one or a linked sample has no answer; a
 * bevaring type needs no tons (it is never waste), but still blocks on
 * review or a sample.
 */
export function blockReasons(t: SurveyType, samples: readonly Sample[]): BlockReason[] {
  const out: BlockReason[] = [];
  if (t.environment_status === 'afventer' || samplesOfType(t, samples).some((s) => s.stage !== 'svar')) out.push('sample');
  if (t.review_status !== 'approved') out.push('review');
  if (t.mass_t === null && t.treatment !== 'bevaring') out.push('mass');
  return out;
}

export interface BlockingItem {
  typeId: number;
  name: string;
  /** Null when the server names a type the case does not hold (yet). */
  partCount: number | null;
  /** "i kø", "godkendt"; null as `partCount`. */
  reviewText: string | null;
  reasons: BlockReason[];
}

function sortReasons(reasons: Iterable<BlockReason>): BlockReason[] {
  const set = new Set(reasons);
  return REASON_ORDER.filter((r) => set.has(r));
}

function itemOf(t: SurveyType, reasons: BlockReason[]): BlockingItem {
  return {
    typeId: t.id,
    name: t.name,
    partCount: t.parts.length,
    reviewText: REVIEW_LABEL[t.review_status].toLowerCase(),
    reasons,
  };
}

/** The blocking types, with their reasons (see the module comment for who decides). */
export function blockingItems(
  types: readonly SurveyType[],
  samples: readonly Sample[],
  fractions: SurveyFractions | null,
): BlockingItem[] {
  if (!fractions) {
    return reportedTypes(types).flatMap((t) => {
      const reasons = blockReasons(t, samples);
      return reasons.length > 0 ? [itemOf(t, reasons)] : [];
    });
  }
  const byId = new Map(types.map((t) => [t.id, t]));
  return fractions.blocking.flatMap((b) => {
    const t = byId.get(b.type_id);
    if (!t) return [{ typeId: b.type_id, name: b.name, partCount: null, reviewText: null, reasons: [b.reason] }];
    return [itemOf(t, sortReasons([...blockReasons(t, samples), b.reason]))];
  });
}

/** The blocking types the case holds, in list order: what Gennemgang can open and J/K walk. */
export function heldBlockingIds(items: readonly BlockingItem[]): number[] {
  return items.filter((i) => i.partCount !== null).map((i) => i.typeId);
}

/** "1 type" / "3 typer". */
export function blockingCountText(n: number): string {
  return n === 1 ? '1 type' : `${n} typer`;
}

/** A row's subline: "3 dele · i kø"; '' when the type is not held. */
export function blockingDetail(item: BlockingItem): string {
  if (item.partCount === null) return '';
  const parts = item.partCount === 1 ? '1 del' : `${item.partCount} dele`;
  return item.reviewText ? `${parts} · ${item.reviewText}` : parts;
}
